// Google AdSense provider (publisher ca-pub-7118157221994432, id via config).
// POLICY NOTES (verified 2026-08-21, see memory/ad-gate-v1):
//   - Units render only in normal page flow — NEVER inside modals/overlays
//     (placement policy) and never on pages for paid users (script not loaded).
//   - No popunders anywhere on the site, no rewarding users for ad views.
//   - cfg.adTest adds data-adtest="on" (staging/QA builds) so impressions from
//     testing are never counted as invalid traffic.
import { adsConfig } from './../config'
import { detectAdblockBait } from './../adblock'
import type { AdProvider, FillResult, LoadResult } from './types'

declare global {
  interface Window { adsbygoogle?: unknown[] }
}

let sdkPromise: Promise<LoadResult> | null = null

function loadSdk(): Promise<LoadResult> {
  const { clientId, sdkTimeoutMs } = adsConfig()
  if (!clientId) return Promise.resolve('timeout')
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise<LoadResult>((resolve) => {
    let done = false
    const finish = (r: LoadResult) => {
      if (!done) { done = true; resolve(r) }
      if (r !== 'ok') sdkPromise = null // allow retry after wall/timeout
    }
    const s = document.createElement('script')
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(clientId)}`
    s.async = true
    s.crossOrigin = 'anonymous'
    // Killed request with the network up = adblock signal → wall (policy-dependent).
    s.onerror = () => finish(navigator.onLine ? 'blocked' : 'timeout')
    s.onload = () => finish('ok')
    document.head.appendChild(s)
    // Slow load without an error is NOT an adblock signal — fail open upstream.
    setTimeout(() => finish('timeout'), sdkTimeoutMs)
  })
  return sdkPromise
}

/**
 * Render one responsive display unit. AdSense stamps data-ad-status on the
 * <ins> once resolved ('filled' / 'unfilled') — we poll for it up to
 * adTimeoutMs so callers can hide empty hosts instead of showing blank boxes.
 */
async function renderUnit(host: HTMLElement, slot: string, size: { width: number; height: number }): Promise<FillResult> {
  const cfg = adsConfig()
  const sdk = await loadSdk()
  if (sdk === 'blocked') return 'blocked'
  if (sdk === 'timeout') return 'unfilled'
  const ins = document.createElement('ins')
  ins.className = 'adsbygoogle'
  // width 0 = fixed-height responsive (Google picks a horizontal creative for
  // the available width); otherwise a pinned WxH creative. Never data-ad-format
  // auto — free heights could outgrow the host, and clipping ads is a policy hit.
  ins.style.cssText = size.width
    ? `display:inline-block;width:${size.width}px;height:${size.height}px`
    : `display:block;width:100%;height:${size.height}px`
  ins.dataset.adClient = cfg.clientId
  ins.dataset.adSlot = slot
  if (cfg.adTest) ins.dataset.adtest = 'on'
  host.replaceChildren(ins)
  try {
    ;(window.adsbygoogle = window.adsbygoogle || []).push({})
  } catch {
    return 'unfilled'
  }
  // Two-phase wait: data-ad-status only appears after the ad request, and in
  // the EEA the request itself is held until the visitor answers the Google
  // consent dialog (Privacy & messaging). So while the SDK HAS processed the
  // ins (adsbygoogle-status set) but no verdict exists yet, keep waiting well
  // past adTimeoutMs — the user is likely still reading the consent prompt.
  const CONSENT_WAIT_MS = 120_000
  const start = Date.now()
  for (;;) {
    const status = ins.dataset.adStatus
    if (status === 'filled') return 'filled'
    if (status === 'unfilled') return 'unfilled'
    const processed = ins.dataset.adsbygoogleStatus !== undefined
    if (Date.now() - start > (processed ? CONSENT_WAIT_MS : cfg.adTimeoutMs)) return 'unfilled'
    await new Promise((r) => setTimeout(r, 250))
  }
}

export const adsenseProvider: AdProvider = {
  id: 'adsense',
  loadSdk,
  renderUnit,
  detectAdblock: detectAdblockBait,
}
